import { useRef } from "react";
import "./EventTimePicker.css";

const DEFAULT_EVENT_TIME = "19:30";

const splitTime = (value) => {
  const normalized = /^([01]\d|2[0-3]):([0-5]\d)$/.test(String(value || ""))
    ? String(value)
    : DEFAULT_EVENT_TIME;
  return normalized.split(":");
};

const wrapValue = (value, maximum) => {
  if (value > maximum) return 0;
  if (value < 0) return maximum;
  return value;
};

const Chevron = ({ direction }) => (
  <svg viewBox="0 0 16 10" aria-hidden="true">
    <path d={direction === "up" ? "M2 8 8 2l6 6" : "m2 2 6 6 6-6"} />
  </svg>
);

// Arrow-only: the boxes are read-only so the phone keyboard never opens.
// One tap moves by 1; quick taps in a row on a minute arrow jump to the next 5.
const FAST_TAP_MS = 450;

const nextValue = (current, amount, maximum, fastStep) => {
  if (!fastStep) return wrapValue(current + amount, maximum);
  const snapped = amount > 0
    ? Math.floor(current / fastStep) * fastStep + fastStep
    : Math.ceil(current / fastStep) * fastStep - fastStep;
  return ((snapped % (maximum + 1)) + maximum + 1) % (maximum + 1);
};

const TimeStepper = ({ label, value, maximum, fastStep, onChange }) => {
  const lastTap = useRef({ amount: 0, at: 0 });

  const changeBy = (amount) => {
    const now = Date.now();
    const fast = Boolean(fastStep) && lastTap.current.amount === amount && now - lastTap.current.at < FAST_TAP_MS;
    lastTap.current = { amount, at: now };
    onChange(String(nextValue(Number(value), amount, maximum, fast ? fastStep : 0)).padStart(2, "0"));
  };

  return (
    <div className="event-time-stepper">
      <button type="button" aria-label={`הגדלת ${label}`} onClick={() => changeBy(1)}>
        <Chevron direction="up" />
      </button>
      <input
        type="text"
        role="spinbutton"
        readOnly
        inputMode="none"
        autoComplete="off"
        aria-label={label}
        aria-valuemin="0"
        aria-valuemax={maximum}
        aria-valuenow={Number(value)}
        value={value}
        onKeyDown={(event) => {
          if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            event.preventDefault();
            changeBy(event.key === "ArrowUp" ? 1 : -1);
          }
        }}
      />
      <button type="button" aria-label={`הקטנת ${label}`} onClick={() => changeBy(-1)}>
        <Chevron direction="down" />
      </button>
    </div>
  );
};

const EventTimePicker = ({ value, onChange, disabled = false }) => {
  const [hour, minute] = splitTime(value);

  return (
    <fieldset className="event-time-picker" dir="ltr" disabled={disabled} aria-label="בחירת שעת האירוע בפורמט 24 שעות">
      <TimeStepper
        label="שעה"
        value={hour}
        maximum={23}
        onChange={(nextHour) => onChange(`${nextHour}:${minute}`)}
      />
      <span className="event-time-picker__separator" aria-hidden="true">:</span>
      <TimeStepper
        label="דקות"
        value={minute}
        maximum={59}
        fastStep={5}
        onChange={(nextMinute) => onChange(`${hour}:${nextMinute}`)}
      />
    </fieldset>
  );
};

export { DEFAULT_EVENT_TIME };
export default EventTimePicker;
